import { useCallback, useEffect, useId, useRef, useState, type RefObject } from "react";

/**
 * Row overflow menu (Schedule Wave 2 design board, Implement rule 6: "lifecycle seating").
 *
 * The 2026-08-15 delete/retire lock's verbs did not change; only their ADDRESS did. Discard
 * draft and Retire moved out of headline chrome into this menu so each row keeps one loud
 * control at most, while the confirms, refusal rules, and audit events behind them stay
 * exactly as shipped.
 *
 * Follows the TopbarFeedbackMenu pattern rather than pulling a new dropdown dependency:
 * Escape closes and returns focus to the trigger, an outside pointer press closes, and
 * choosing an item closes before the item's own handler opens whatever comes next (a
 * confirm dialog usually moves focus itself).
 */
export interface RowMenuItem {
  readonly key: string;
  readonly label: string;
  readonly onSelect: () => void;
  /**
   * Destructive items render coral (board CTA hierarchy: "destructive coral text,
   * confirm-gated, lives in overflow menus and dialogs only"). The confirm itself belongs
   * to the caller; this only styles the address.
   */
  readonly destructive?: boolean;
  /** One quiet line under the item, e.g. the lock's cannot-be-undone helper. */
  readonly help?: string;
}

export function RowMenu({
  ariaLabel,
  items,
  triggerLabel = "More actions",
  triggerRef: externalTriggerRef,
}: {
  /** Names the menu for assistive tech, e.g. "Actions for Harborview nights". */
  readonly ariaLabel: string;
  readonly items: readonly RowMenuItem[];
  readonly triggerLabel?: string;
  /**
   * Lets a caller hold the trigger element. A dialog opened from this menu can then
   * guarantee focus returns here even if whatever had focus is gone by the time it
   * closes, which is the difference between a keyboard operator continuing and being
   * stranded on the body after a destructive confirm.
   */
  readonly triggerRef?: RefObject<HTMLButtonElement | null>;
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const ownTriggerRef = useRef<HTMLButtonElement>(null);
  const triggerRef = externalTriggerRef ?? ownTriggerRef;
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const menuId = useId();
  const helpIdBase = useId();

  const close = useCallback(() => setOpen(false), []);

  /** Move roving focus inside the menu, wrapping at both ends. */
  const focusItem = useCallback((index: number) => {
    const buttons = itemRefs.current.filter((node): node is HTMLButtonElement => node !== null);
    if (buttons.length === 0) return;
    const wrapped = ((index % buttons.length) + buttons.length) % buttons.length;
    buttons[wrapped]?.focus();
  }, []);

  // Opening with the keyboard has to land somewhere: a menu whose items can only be
  // reached by Tab is not a menu, it is a list that lied about its role.
  useEffect(() => {
    if (!open) return;
    setActiveIndex(0);
    focusItem(0);
  }, [focusItem, open]);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        close();
        triggerRef.current?.focus();
        return;
      }
      // Arrow keys, Home, and End are the menu contract the role promises.
      const buttons = itemRefs.current.filter((node): node is HTMLButtonElement => node !== null);
      const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
      if (event.key === "ArrowDown") {
        event.preventDefault();
        focusItem(current + 1);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        focusItem(current - 1);
      } else if (event.key === "Home") {
        event.preventDefault();
        focusItem(0);
      } else if (event.key === "End") {
        event.preventDefault();
        focusItem(buttons.length - 1);
      }
    }
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) {
        return;
      }
      close();
    }
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [close, focusItem, open, triggerRef]);

  if (items.length === 0) {
    return null;
  }

  return (
    <div className="rowmenu">
      <button
        ref={triggerRef}
        type="button"
        className="rowmenu__trigger"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((current) => !current)}
      >
        <span aria-hidden="true">{triggerLabel === "More actions" ? "⋯" : triggerLabel}</span>
      </button>
      {open ? (
        <div ref={menuRef} id={menuId} className="rowmenu__menu" role="menu" aria-label={ariaLabel}>
          {items.map((item, index) => {
            // `role="none"` on the wrapper: anything between a menu and its items that
            // keeps a generic role breaks the menu contract for assistive tech.
            const helpId = item.help !== undefined ? `${helpIdBase}-${item.key}` : undefined;
            return (
              <div key={item.key} className="rowmenu__row" role="none">
                <button
                  ref={(node) => {
                    itemRefs.current[index] = node;
                  }}
                  type="button"
                  role="menuitem"
                  // Roving tabindex: the menu owns arrow-key movement, so exactly one
                  // item is a tab stop and it is the one focus is actually on.
                  tabIndex={index === activeIndex ? 0 : -1}
                  onFocus={() => setActiveIndex(index)}
                  aria-describedby={helpId}
                  className={`rowmenu__item${item.destructive === true ? " rowmenu__item--danger" : ""}`}
                  onClick={() => {
                    close();
                    // Focus returns to the trigger BEFORE the item runs, for two reasons:
                    // the item's own element is about to unmount with the menu, and a
                    // dialog opened by the item records the currently focused element as
                    // its restore target. Without this, dismissing that dialog would drop
                    // focus to the body and strand a keyboard operator.
                    triggerRef.current?.focus();
                    item.onSelect();
                  }}
                >
                  {item.label}
                </button>
                {item.help !== undefined ? (
                  <p id={helpId} className="rowmenu__help">
                    {item.help}
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

/** Convenience for callers assembling a menu from optional entries. */
export function rowMenuItems(items: readonly (RowMenuItem | null)[]): RowMenuItem[] {
  return items.filter((item): item is RowMenuItem => item !== null);
}
